import { Component, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';

// Importaciones de entornos.
import { environment } from '../../../../environments/environment';

// Importaciones de servicios.
import { ConfigurationService } from '../../../services/configuration.service';
import { LogService } from '../../../services/log.service';

// Importaciones de interfaces.
import { IConfiguration } from '../../../interfaces/configuration.interface';
import { IConfigurationResponse } from '../../../interfaces/configuration-response.interface';

// Importaciones de modelos.
import { Configuration } from '../../../models/configuration.model';
import { Log } from '../../../models/log.model';
import { passwordMatchValidator } from '../../../validators/password-match.validator';

/**
 * Componente para la configuración general.
 * Permite gestionar y actualizar la información general.
 */
@Component({
  selector: 'app-general-settings',
  templateUrl: './general-settings.component.html',
  standalone: false,
})
export class GeneralSettingsComponent implements OnInit {
  /**
   * Indica si la nueva contraseña debe estar oculta en el campo de entrada.
   *
   * Esta propiedad controla si la nueva contraseña introducida por el usuario debe ser mostrada
   * o estar oculta en el campo de entrada (por ejemplo, representada por asteriscos). Se establece
   * en `true` de manera predeterminada para ocultar la contraseña al usuario.
   */
  emailPasswordPasswordHide: boolean = true;

  /**
   * Indica si la confirmación de la nueva contraseña debe estar oculta en el campo de entrada.
   *
   * Similar a la propiedad `emailPasswordPasswordHide`, esta propiedad controla si el campo
   * de confirmación de contraseña debe mostrar o ocultar la contraseña introducida. Se establece
   * en `true` por defecto, para ocultar la confirmación de la contraseña.
   */
  emailPasswordConfirmPasswordHide: boolean = true;

  /**
   * Indica si las configuraciones están cargando.
   *
   * Esta propiedad se utiliza para representar el estado de carga de las configuraciones.
   * Se cambia a `true` cuando se están cargando las configuraciones, lo que puede ser útil
   * para mostrar un indicador de carga o deshabilitar la interacción del usuario mientras
   * las configuraciones se están cargando.
   */
  loadingConfigurations: boolean = false;

  /**
   * Indica si se está guardando la configuración del correo electrónico.
   *
   * Esta propiedad se utiliza para controlar el estado de la operación de guardado de la
   * configuración del correo electrónico. Se establece en `false` de forma predeterminada
   * y se cambia a `true` cuando la configuración del correo electrónico está en proceso de guardado.
   */
  loadingSaveEmailConfigurations: boolean = false;

  /**
   * Indica si se está guardando la configuración de la contraseña del correo electrónico.
   *
   * Esta propiedad indica si la operación de guardado de la configuración de la contraseña del
   * correo electrónico está en curso. Se establece en `false` de manera predeterminada y se cambia
   * a `true` mientras se está guardando la configuración.
   */
  loadingSaveEmailPasswordConfiguration: boolean = false;

  /**
   * Indica si se está guardando la configuración de activación.
   *
   * Esta propiedad se utiliza para controlar si la operación de guardado de la configuración de
   * activación por correo electrónico está en curso. Se inicializa en `false` y se establece a `true`
   * durante la operación de guardado.
   */
  loadingSaveActivationConfigurations: boolean = false;

  /**
   * Almacena los errores ocurridos al guardar la configuración del correo electrónico.
   *
   * Esta propiedad es un arreglo que contiene los mensajes de error generados durante el proceso
   * de guardado de la configuración del correo electrónico. Inicialmente, es un arreglo vacío.
   */
  errorsOnSaveEmailConfigurations: string[] = [];

  /**
   * Almacena los errores ocurridos al guardar la configuración de la contraseña del correo electrónico.
   *
   * Al igual que `errorsOnSaveEmailConfigurations`, esta propiedad almacena los errores que se
   * producen durante el proceso de guardado de la configuración de la contraseña del correo electrónico.
   * Se inicializa como un arreglo vacío.
   */
  errorsOnSaveEmailPasswordConfiguration: string[] = [];

  /**
   * Almacena los errores ocurridos al guardar la configuración de activación.
   *
   * Esta propiedad almacena los errores que ocurren durante el proceso de guardado de la
   * configuración de activación por correo electrónico. Se inicializa como un arreglo vacío.
   */
  errorsOnSaveActivationConfigurations: string[] = [];

  /**
   * Formulario para la configuración del correo electrónico.
   *
   * Este formulario contiene los campos necesarios para configurar los parámetros básicos
   * del correo electrónico, como el host, el puerto, la opción de conexión segura, el usuario
   * y el nombre a mostrar en los correos electrónicos. Algunos campos, como el usuario, tienen validación
   * para asegurarse de que el formato del correo electrónico sea correcto.
   */
  formEmail: FormGroup = new FormGroup({
    host: new FormControl('', Validators.required),
    port: new FormControl(),
    secure: new FormControl(),
    user: new FormControl('', [
      Validators.required,
      Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$'),
    ]),
    displayName: new FormControl(),
  });

  /**
   * Formulario para la contraseña de la configuración del correo electrónico.
   *
   * Este formulario permite ingresar la contraseña y la confirmación de la contraseña del correo electrónico.
   * Se incluye un validador personalizado para verificar que ambas contraseñas coincidan antes de permitir
   * la actualización de la configuración.
   */
  formEmailPassword: FormGroup = new FormGroup(
    {
      password: new FormControl('', Validators.required),
      confirmPassword: new FormControl('', Validators.required),
    },
    { validators: passwordMatchValidator('password', 'confirmPassword') }
  );

  /**
   * Formulario para la configuración de activación por correo electrónico.
   *
   * Este formulario contiene el campo para la dirección de correo electrónico de confirmación,
   * que es obligatorio y debe tener un formato válido de correo electrónico.
   */
  formActivation: FormGroup = new FormGroup({
    confirmationEmail: new FormControl('', [
      Validators.required,
      Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$'),
    ]),
  });

  /**
   * Obtiene el control del formulario correspondiente al host del correo electrónico.
   *
   * Este getter accede al control de formulario asociado al campo 'host' dentro del formulario
   * de configuración del correo electrónico. Si el formulario no tiene un control para 'host',
   * devolverá `null`.
   */
  get formEmailHost(): AbstractControl | null {
    return this.formEmail.get('host');
  }

  /**
   * Obtiene el control del formulario correspondiente al puerto del correo electrónico.
   *
   * Este getter accede al control de formulario asociado al campo 'port' dentro del formulario
   * de configuración del correo electrónico. Si el formulario no tiene un control para 'port',
   * devolverá `null`.
   */
  get formEmailPort(): AbstractControl | null {
    return this.formEmail.get('port');
  }

  /**
   * Obtiene el control del formulario correspondiente a la opción de conexión segura.
   *
   * Este getter accede al control de formulario asociado al campo 'secure' dentro del formulario
   * de configuración del correo electrónico. Si el formulario no tiene un control para 'secure',
   * devolverá `null`.
   */
  get formEmailSecure(): AbstractControl | null {
    return this.formEmail.get('secure');
  }

  /**
   * Obtiene el control del formulario correspondiente al usuario del correo electrónico.
   *
   * Este getter accede al control de formulario asociado al campo 'user' dentro del formulario
   * de configuración del correo electrónico. Si el formulario no tiene un control para 'user',
   * devolverá `null`.
   */
  get formEmailUser(): AbstractControl | null {
    return this.formEmail.get('user');
  }

  /**
   * Obtiene el control del formulario correspondiente al nombre a mostrar en los correos enviados.
   *
   * Este getter accede al control de formulario asociado al campo 'displayName' dentro del formulario
   * de configuración del correo electrónico. Si el formulario no tiene un control para 'displayName',
   * devolverá `null`.
   */
  get formEmailDisplayName(): AbstractControl | null {
    return this.formEmail.get('displayName');
  }

  /**
   * Obtiene el control del formulario para la contraseña del correo electrónico.
   *
   * Este getter devuelve el control de formulario asociado al campo 'password' del formulario
   * de configuración del correo electrónico. Si el formulario no tiene un control para 'password',
   * devolverá `null`.
   */
  get formEmailPasswordPassword(): AbstractControl | null {
    return this.formEmailPassword.get('password');
  }

  /**
   * Obtiene el control del formulario para la confirmación de la contraseña del correo electrónico.
   *
   * Este getter devuelve el control de formulario asociado al campo 'confirmPassword' del formulario
   * de configuración del correo electrónico. Si el formulario no tiene un control para 'confirmPassword',
   * devolverá `null`.
   */
  get formEmailPasswordConfirmPassword(): AbstractControl | null {
    return this.formEmailPassword.get('confirmPassword');
  }

  /**
   * Obtiene el control del formulario para el campo de correo electrónico de confirmación de activación.
   *
   * Este getter accede al control de formulario asociado al campo 'confirmationEmail' dentro del formulario
   * de activación. Si el formulario no tiene un control para 'confirmationEmail', devolverá `null`.
   */
  get formActivationConfirmationEmail(): AbstractControl | null {
    return this.formActivation.get('confirmationEmail');
  }

  /**
   * Constructor del componente.
   *
   * @param configurationService - Servicio encargado de la gestión de datos relacionados con la configuración.
   * @param logService - Servicio para gestionar los registros.
   * @param snackBar - Servicio de Angular Material utilizado para mostrar mensajes emergentes en la interfaz de usuario.
   */
  constructor(
    private configurationService: ConfigurationService,
    private logService: LogService,
    private snackBar: MatSnackBar
  ) {}

  /**
   * Método del ciclo de vida `OnInit` de Angular.
   * Se ejecuta una vez que el componente ha sido inicializado.
   *
   * Aquí es común realizar tareas de initialization, como cargar datos o realizar peticiones al backend.
   */
  ngOnInit(): void {
    this.getConfigurations();
  }

  /**
   * Método asíncrono que obtiene las configuraciones.
   *
   * Realiza una petición al servicio correspondiente para recuperar la información.
   * En caso de éxito, los datos se asignan a las propiedades correspondientes del componente.
   */
  async getConfigurations(): Promise<void> {
    this.loadingConfigurations = true;

    try {
      const configurationResponse: IConfigurationResponse =
        await this.configurationService.getAll();

      const configurationsMap: Record<string, (value: string) => void> = {
        email_host: (value) => this.formEmailHost?.setValue(value),
        email_port: (value) => this.formEmailPort?.setValue(Number(value)),
        email_secure: (value) =>
          this.formEmailSecure?.setValue(value === 'true'),
        email_user: (value) => this.formEmailUser?.setValue(value),
        email_display_name: (value) =>
          this.formEmailDisplayName?.setValue(value),
        activation_confirmation_email: (value) =>
          this.formActivationConfirmationEmail?.setValue(value),
      };

      configurationResponse.configurations.forEach(({ name, value }) => {
        if (name && configurationsMap[name]) {
          configurationsMap[name](value ?? '');
        }
      });
    } finally {
      this.loadingConfigurations = false;
    }
  }

  /**
   * Guarda la configuración del correo electrónico.
   *
   * Esta función verifica si el formulario del correo electrónico es válido y, si lo es,
   * guarda la configuración correspondiente. Si la configuración ya existe, se actualiza,
   * de lo contrario, se crea una nueva entrada.
   */
  async saveEmailConfigurations(): Promise<void> {
    if (this.formEmail.invalid) {
      return;
    }

    this.loadingSaveEmailConfigurations = true;
    this.errorsOnSaveEmailConfigurations = [];

    const configurationsArray: [string, string][] = [
      ['email_host', this.formEmailHost?.value ?? ''],
      ['email_port', String(this.formEmailPort?.value ?? '')],
      ['email_display_name', this.formEmailDisplayName?.value ?? ''],
      ['email_user', this.formEmailUser?.value ?? ''],
      ['email_secure', String(this.formEmailSecure?.value ?? '')],
    ];

    const promises = configurationsArray.map(async ([name, value]) => {
      try {
        const configuration = new Configuration({ name, value });
        return await this.configurationService.upsert(configuration);
      } catch (errors: any) {
        this.errorsOnSaveEmailConfigurations.push(...errors);
        return Promise.reject(errors);
      }
    });

    await Promise.allSettled(promises);

    if (!this.errorsOnSaveEmailConfigurations.length) {
      // Añadir un registro de guardado de la configuración del correo electrónico.
      try {
        await this.logService.create(
          new Log({
            description: 'Ha guardado la configuración del correo electrónico.',
          })
        );
      } catch (errors: any) {
        if (!environment.production) {
          console.warn(
            'No se ha podido guardar el registro de guardado de la configuración del correo electrónico.'
          );
        }
      }

      this.snackBar.open(
        'Las configuraciones del correo electrónico se han guardado correctamente.',
        '',
        { duration: 5000 }
      );
    }

    this.loadingSaveEmailConfigurations = false;
  }

  /**
   * Guarda la contraseña de la configuración del correo electrónico.
   *
   * Esta función primero valida si el formulario de la contraseña del correo electrónico es válido.
   * Si es válido, realiza una llamada a la API para actualizar la configuración de la contraseña.
   * En caso de éxito, también guarda un registro de la acción en los logs.
   * Si ocurre un error en cualquier parte del proceso, lo maneja y muestra un mensaje en la interfaz de usuario.
   */
  async saveEmailPasswordConfiguration(): Promise<void> {
    if (this.formEmailPassword.invalid) {
      return;
    }

    this.loadingSaveEmailPasswordConfiguration = true;
    this.errorsOnSaveEmailPasswordConfiguration = [];

    try {
      await this.configurationService.upsertEmailPassword(
        this.formEmailPasswordPassword?.value ?? ''
      );

      // Añadir un registro del guardado de la contraseña de la configuración del correo electrónico.
      try {
        await this.logService.create(
          new Log({
            description:
              'Ha guardado la contraseña de la configuración del correo electrónico.',
          })
        );
      } catch (errors: any) {
        if (!environment.production) {
          console.warn(
            'No se ha podido guardar el registro del guardado de la contraseña de la configuración del correo electrónico.'
          );
        }
      }

      this.snackBar.open(
        'La contraseña de la configuración del correo electrónico se han guardado correctamente.',
        '',
        { duration: 5000 }
      );
    } catch (errors: any) {
      this.errorsOnSaveEmailPasswordConfiguration.push(...errors);
    }

    this.loadingSaveEmailPasswordConfiguration = false;
  }

  /**
   * Guarda la configuración de la activación.
   *
   * Esta función verifica si el formulario de activación es válido y, si lo es,
   * guarda la configuración correspondiente. Si la configuración ya existe, se actualiza,
   * de lo contrario, se crea una nueva entrada.
   */
  async saveActivationConfigurations(): Promise<void> {
    if (this.formActivation.invalid) {
      return;
    }

    this.loadingSaveActivationConfigurations = true;
    this.errorsOnSaveActivationConfigurations = [];

    const configuration = new Configuration({
      name: 'activation_confirmation_email',
      value: this.formActivationConfirmationEmail?.value ?? '',
    });

    try {
      await this.configurationService.upsert(configuration);

      // Añadir un registro de guardado de la configuración de la activación.
      try {
        await this.logService.create(
          new Log({
            description: 'Ha guardado la configuración de la activación.',
          })
        );
      } catch (errors: any) {
        if (!environment.production) {
          console.warn(
            'No se ha podido guardar el registro de la configuración de la activación.'
          );
        }
      }

      this.snackBar.open(
        'Las configuraciones de l’activación se han guardado correctamente.',
        '',
        { duration: 5000 }
      );
    } catch (errors: any) {
      this.errorsOnSaveActivationConfigurations.push(...errors);
    }

    this.loadingSaveActivationConfigurations = false;
  }
}
